import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Download, AlertTriangle } from "lucide-react";
import SEO from "../components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 8;

const PRETTY = {
  "personal-finance-dashboard.xlsx": "Personal Finance Dashboard",
  "retirement-planner.xlsx": "Retirement Planner",
  "net-worth-tracker.xlsx": "Net Worth Tracker",
  "debt-freedom-planner.xlsx": "Debt Freedom Planner",
  "mortgage-planner.xlsx": "Mortgage Planner",
  "tfsa-rrsp-planner.xlsx": "TFSA & RRSP Planner",
  "family-financial-planner.xlsx": "Family Financial Planner",
  "fire-planner.xlsx": "FIRE Planner",
  "myfinancesguru-all-planners.zip": "All 8 planners (zip)",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error | timeout
  const [files, setFiles] = useState([]);

  const poll = useCallback(async (tries) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setFiles(data.files || []);
        setState("paid");
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
    } catch (e) { /* keep trying */ }
    if (tries >= MAX_TRIES) { setState("timeout"); return; }
    setTimeout(() => poll(tries + 1), 2000);
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <>
      <SEO title="Thank you — MyFinancesGuru Tools" description="Your purchase is complete." path="/payment/success" noindex />
      <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand" />
            <h1 className="mt-6 font-heading text-3xl font-bold text-foreground">Confirming your payment…</h1>
            <p className="mt-3 text-muted-foreground">This only takes a moment.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-brand" />
            <h1 className="mt-6 font-heading text-4xl font-bold text-foreground">Thank you! 🎉</h1>
            <p className="mt-3 text-lg text-muted-foreground">Your download is ready below. A copy is yours to keep forever.</p>
            <div className="mt-8 flex flex-col items-center gap-3">
              {files.map((f) => (
                <a
                  key={f}
                  href={`${API}/downloads/paid/${sessionId}/${f}`}
                  data-testid={`paid-download-${f}`}
                  className="inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-full bg-action px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-action-hover"
                >
                  <Download className="h-4 w-4" /> Download {PRETTY[f] || f}
                </a>
              ))}
            </div>
            <p className="mt-8 text-sm text-muted-foreground">
              Trouble downloading? Email us and we'll sort it out. <Link to="/products" className="font-semibold text-brand hover:text-action">Browse more planners →</Link>
            </p>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-error">
            <AlertTriangle className="mx-auto h-12 w-12 text-action" />
            <h1 className="mt-6 font-heading text-3xl font-bold text-foreground">
              {state === "timeout" ? "Still processing…" : "Something went wrong"}
            </h1>
            <p className="mt-3 text-muted-foreground">
              {state === "timeout"
                ? "Your payment is taking longer than usual to confirm. Refresh this page in a minute — if you were charged, your download will appear here."
                : "We couldn't confirm this payment. If you were charged, please contact us and we'll help right away."}
            </p>
            <Link to="/products" className="mt-8 inline-flex rounded-full border border-brand px-6 py-3 text-sm font-semibold text-brand hover:bg-brand/5">
              Back to products
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
